<template>
	<input class='form-control' ref='input' :value='value' :name='name' :type='inputType' @input="updateValue($event.target.value)" />
</template>

<script>
	/**
	 * The array of names of the tooltip messages of the datetime picker.
	 *
	 * This is a constant and should not be modified.
	 */
	var DATETIME_PICKER_TOOLTIPS = [
		"today", "clear", "close",
		"selectMonth", "prevMonth", "nextMonth",
		"selectYear", "prevYear", "nextYear",
		"selectDecade", "prevDecade", "nextDecade",
		"prevCentury", "nextCentury",
		"pickHour", "incrementHour", "decrementHour",
		"pickMinute", "incrementMinute", "decrementMinute",
		"pickSecond", "incrementSecond", "decrementSecond",
		"togglePeriod", "selectTime"
	];

	/**
	 * The default language used by this component.
	 */
	var DEFAULT_LANGUAGE = "da-DK";

	/**
	 * A datetime picker control.
	 *
	 * @param model
	 *    the model bind to the control, which must be a two way binding variable.
	 * @param type
	 *    the optional type of this datetime picker control. Available values are
	 *    - "datetime": Indicating that this control is a datetime picker,
	 *    - "date": Indicating that this control is a date picker,
	 *    - "time": Indicating that this control is a time picker.
	 *    Default value is "datetime".
	 * @param language
	 *    the optional language code used to localize the control, which must be
	 *    a valid language code supported by the moment.js library. If it is not set,
	 *    and the [vue-i18n](https://github.com/Haixing-Hu/vue-i18n) plugin is used,
	 *    the component will use the language code `$language` provided by the
	 *    [vue-i18n](https://github.com/Haixing-Hu/vue-i18n) plugin; otherwise, the
	 *    component will use the default value "en-US".
	 * @param datetimeFormat
	 *    the optional format of the datetime this component should display, which
	 *    must be a valid datetime format of the moment.js library. This property
	 *    only works when the "type" property is "datetime". Default value is
	 *    "YYYY-MM-DD HH:mm:ss".
	 * @param dateFormat
	 *    the optional format of the date this component should display, which
	 *    must be a valid datetime format of the moment.js library. This property
	 *    only works when the "type" property is "date". Default value is
	 *    "YYYY-MM-DD".
	 * @param timeFormat
	 *    the optional format of the time this component should display, which
	 *    must be a valid datetime format of the moment.js library. This property
	 *    only works when the "type" property is "time". Default value is
	 *    "HH:mm:ss".
	 * @param name
	 *    the optional name of the selection control.
	 * @param onChange
	 *    the optional event handler triggered when the value of the datetime picker
	 *    was changed. If this parameter is presented and is not null, it must be a
	 *    function which accept one argument: the new date time, which is a moment
	 *    object.
	 */
	export default {
		name: "datetime-picker",
		replace: true,
		inherit: false,
		props: {
			value: {
				required: false,
			},
			options: {
				type: Object,
				required: false,
				default: function(){return {}},
			},
			type: {
				type: String,
				required: false,
				default: "datetime"
			},
			language: {
				type: String,
				required: false,
				default: ""
			},
			datetimeFormat: {
				type: String,
				required: false,
				default: "YYYY-MM-DD HH:mm:ss"
			},
			dateFormat: {
				type: String,
				required: false,
				default: "YYYY-MM-DD"
			},
			timeFormat: {
				type: String,
				required: false,
				default: "HH:mm:ss"
			},
			name: {
				type: String,
				required: false,
				default: ""
			},
			onChange: {
				required: false,
				default: null
			}
		},
		computed: {
			inputType: function() {
				var check = false;
				(function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
				return check ? this.type : "text";
				// return this.type;
			}
		},
		beforeCompile: function() {
			this.isChanging = false;
			this.control = null;
		},
		mounted: function() {
			// console.debug("datetime-picker.ready");
			var options = _.assign({
				useCurrent: false,
				showClear: false,
				showClose: true,
				showTodayButton: true,
				allowInputToggle: true,
				icons: {
					time: 'fa fa-clock-o',
					date: 'fa fa-calendar',
					up: 'fa fa-chevron-up',
					down: 'fa fa-chevron-down',
					previous: 'fa fa-chevron-left',
					next: 'fa fa-chevron-right',
					today: 'fa fa-dot-circle-o',
					clear: 'fa fa-trash',
					close: 'fa fa-times'
				}
			}, this.options);
			// set the locale
			var language = this.language || this.$language || DEFAULT_LANGUAGE;
			options.locale = this.getLanguageCode(language);
			// set the format
			switch (this.type) {
			case "date":
				options.format = this.dateFormat;
				break;
			case "time":
				options.format = this.timeFormat;
				break;
			case "datetime":
			default:
				options.format = this.datetimeFormat;
				break;
			}
			// use the vue-i18n plugin for localize the tooltips
			if (this.$i18n && this.$i18n.datetime_picker) {
				var messages = this.$i18n.datetime_picker;
				var tooltips = $.fn.datetimepicker.defaults.tooltips;
				for (var i = 0; i < DATETIME_PICKER_TOOLTIPS.length; ++i) {
					var name = DATETIME_PICKER_TOOLTIPS[i];
					if (messages[name]) {
						tooltips[name] = messages[name];    // localize
					}
				}
				options.tooltips = tooltips;
			}
			// create the control
			el = jQuery(this.$el);
			if (el.parent().hasClass("input-group")) {
				el = el.parent();
			}
			el.datetimepicker(options);
			this.control = el.data("DateTimePicker");
			// set the date to the current value of the model
			this.control.date(this.value);
			var self = this;
			el.on("dp.change", function (e) {
				if (e.date) {
					self.updateValue(e.date.format(options.format));
				} else {
					self.updateValue("");
				}
			});
		},

		watch: {
			options: function(newOpts) {
				this.control.options(newOpts);
			},
		},

		methods: {
			updateValue: function(value) {
				this.$emit('input', value);
			},
			/**
			 * Gets the language code from the "language-country" locale code.
			 *
			 * The function will strip the language code before the first "-" of a
			 * locale code. For example, pass "en-US" will returns "en". But for some
			 * special locales, the function reserves the locale code. For example,
			 * the "zh-CN" for the simplified Chinese and the "zh-TW" for the
			 * traditional Chinese.
			 *
			 * @param locale
			 *    A locale code.
			 * @return
			 *    the language code of the locale.
			 */
			getLanguageCode: function(locale) {
				if (locale === null || locale.length === 0) {
					return "en";
				}
				if (locale.length <= 2) {
					return locale;
				} else {
					switch (locale) {
						case "zh-CN":
						case "zh-TW":
						case "ar-MA":
						case "ar-SA":
						case "ar-TN":
						case "de-AT":
						case "en-AU":
						case "en-CA":
						case "en-GB":
						case "fr-CA":
						case "hy-AM":
						case "ms-MY":
						case "pt-BR":
						case "sr-CYRL":
						case "tl-PH":
						case "tzm-LATN":
						case "tzm":
							return locale.toLowerCase();
						default:
							// reserve only the first two letters language code
							return locale.substr(0, 2);
					}
				}
			}
		}
	}

</script>


// WEBPACK FOOTER //
// datetime-picker.vue?7e5305fc